import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { usePeriod, isDateInPeriod } from "@/hooks/usePeriod";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { SectionActionsToolbar } from "@/components/SectionActions";
import HabitTipsButton from "@/components/HabitTipsButton";

const HabitsPage = () => {
  const { periodStartDate, getSectionPeriod } = usePeriod();
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [monthlyNote, setMonthlyNote] = useState({ focus: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: "", color: "#1C1917" });
  const [showAllHistory, setShowAllHistory] = useState(false);

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0];
  const sectionPeriod = getSectionPeriod("habits");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [habitsRes, logsRes, noteRes] = await Promise.all([
        api.get(`/habits`),
        api.get(`/habits/logs`, { params: { month: monthStr } }),
        api.get(`/monthly-notes/${monthStr}`)
      ]);
      setHabits(habitsRes.data);
      setLogs(logsRes.data);
      setMonthlyNote(noteRes.data || { focus: "", notes: "" });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHabit = async (e) => {
    e.preventDefault();
    if (!newHabit.name.trim()) {
      toast.error("Inserisci un nome per l'abitudine");
      return;
    }

    try {
      await api.post(`/habits`, newHabit);
      toast.success("Abitudine aggiunta!");
      setNewHabit({ name: "", color: "#1C1917" });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiunta dell'abitudine");
    }
  };

  const handleDeleteHabit = async (habitId) => {
    try {
      await api.delete(`/habits/${habitId}`);
      toast.success("Abitudine eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDotClick = async (habitId, day) => {
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    const existingLog = logs.find(l => l.habit_id === habitId && l.date === date);
    
    // Cycle: empty -> done -> partial -> empty
    let newStatus;
    if (!existingLog || existingLog.status === "skipped") {
      newStatus = "done";
    } else if (existingLog.status === "done") {
      newStatus = "partial";
    } else {
      newStatus = "skipped";
    }

    try {
      await api.post(`/habits/logs`, {
        habit_id: habitId,
        date,
        status: newStatus
      });
      // Update local state
      setLogs(prev => {
        const filtered = prev.filter(l => !(l.habit_id === habitId && l.date === date));
        return [...filtered, { habit_id: habitId, date, status: newStatus }];
      });
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.post(`/monthly-notes`, {
        month: monthStr,
        focus: monthlyNote.focus,
        notes: monthlyNote.notes
      });
      toast.success("Note salvate!");
    } catch (error) {
      toast.error("Errore nel salvataggio delle note");
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const formatMonth = () => {
    return currentMonth.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  // Calculate totals per habit - filtered by period
  const effectivePeriod = showAllHistory ? null : (sectionPeriod || periodStartDate);
  
  // Memoize habit logs map for O(1) lookups
  const logsMap = useMemo(() => {
    const map = {};
    logs.forEach(l => {
      const key = `${l.habit_id}-${l.date}`;
      map[key] = l.status;
    });
    return map;
  }, [logs]);
  
  const getLogStatus = (habitId, day) => {
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    return logsMap[`${habitId}-${date}`] || "skipped";
  };
  
  const getHabitTotal = (habitId) => {
    const habitLogs = logs.filter(l => l.habit_id === habitId && (!effectivePeriod || isDateInPeriod(l.date, effectivePeriod)));
    const done = habitLogs.filter(l => l.status === "done").length;
    const partial = habitLogs.filter(l => l.status === "partial").length;
    return done + (partial * 0.5);
  };

  // Check if a day is in the period
  const isDayInPeriod = (day) => {
    if (showAllHistory) return true;
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    return isDateInPeriod(date, effectivePeriod);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="habits-page">
      {/* Period Banner */}
      {effectivePeriod && (
        <div className="bg-muted/50 border border-border rounded-sm p-3 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Visualizzando dati dal <span className="font-medium text-foreground">{new Date(effectivePeriod).toLocaleDateString('it-IT')}</span>
          </span>
        </div>
      )}

      {/* Section Actions Toolbar */}
      <SectionActionsToolbar
        section="habits"
        currentDate={today}
        showAll={showAllHistory}
        onShowAllChange={setShowAllHistory}
        onDataChanged={fetchData}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="page-title">Abitudini</h1>
          <p className="page-subtitle">Tracker mensile</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-habit-btn" className="rounded-sm w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" /> Nuova Abitudine
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuova Abitudine</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddHabit} className="space-y-4 pt-4">
              <div>
                <label className="text-sm text-muted-foreground">Nome</label>
                <Input
                  data-testid="habit-name-input"
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                  placeholder="Es: Meditazione"
                  className="rounded-sm mt-1"
                />
              </div>
              <Button type="submit" data-testid="save-habit-btn" className="w-full rounded-sm">
                Aggiungi Abitudine
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Mese precedente</span>
        </Button>
        <span className="font-heading text-base sm:text-lg capitalize">{formatMonth()}</span>
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
          <span className="hidden sm:inline mr-1">Mese successivo</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm items-center">
        <span className="text-muted-foreground">Legenda:</span>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="habit-dot habit-dot-done w-4 h-4 sm:w-5 sm:h-5"></div>
          <span>Fatto</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="habit-dot habit-dot-partial w-4 h-4 sm:w-5 sm:h-5"></div>
          <span>Parziale</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="habit-dot habit-dot-empty w-4 h-4 sm:w-5 sm:h-5"></div>
          <span>Saltato</span>
        </div>
      </div>

      {/* Habit Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      ) : (
        <Card className="border border-border shadow-sm rounded-sm" data-testid="habit-grid-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: `${150 + (daysInMonth * 28)}px` }}>
                <thead>
                  <tr className="border-b-2 border-foreground">
                    <th className="text-left p-2 sm:p-4 font-heading text-xs sm:text-sm sticky left-0 bg-background z-10 min-w-[100px] sm:min-w-[150px]">Abitudine</th>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                      const date = `${monthStr}-${String(day).padStart(2, "0")}`;
                      const isToday = date === today;
                      return (
                        <th 
                          key={day} 
                          className={`p-1 text-[10px] sm:text-xs font-mono w-6 sm:w-8 ${isToday ? "bg-foreground text-background" : ""}`}
                        >
                          {day}
                        </th>
                      );
                    })}
                    <th className="p-2 sm:p-4 font-heading text-xs sm:text-sm text-right min-w-[40px] sm:min-w-[60px]">Tot.</th>
                    <th className="w-8 sm:w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {habits.length > 0 ? habits.map((habit) => (
                    <tr key={habit.habit_id} className="border-b border-border" data-testid={`habit-row-${habit.habit_id}`}>
                      <td className="p-2 sm:p-4 text-xs sm:text-sm font-medium sticky left-0 bg-background z-10">{habit.name}</td>
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                        const status = getLogStatus(habit.habit_id, day);
                        const date = `${monthStr}-${String(day).padStart(2, "0")}`;
                        const isToday = date === today;
                        return (
                          <td key={day} className={`p-0.5 sm:p-1 text-center ${isToday ? "bg-muted" : ""}`}>
                            <button
                              onClick={() => handleDotClick(habit.habit_id, day)}
                              className={`w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                                status === "done" ? "border-foreground bg-foreground" :
                                status === "partial" ? "border-muted-foreground bg-gradient-to-t from-muted-foreground from-50% to-transparent to-50%" :
                                "border-border bg-transparent hover:border-muted-foreground"
                              }`}
                              data-testid={`dot-${habit.habit_id}-${day}`}
                              aria-label={`${habit.name} giorno ${day}: ${status}`}
                            />
                          </td>
                        );
                      })}
                      <td className="p-2 sm:p-4 text-right font-mono text-xs sm:text-sm font-medium">
                        {getHabitTotal(habit.habit_id)}
                      </td>
                      <td className="p-1 sm:p-2">
                        <button
                          onClick={() => handleDeleteHabit(habit.habit_id)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`delete-habit-${habit.habit_id}`}
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={daysInMonth + 3} className="p-8 text-center text-muted-foreground text-sm">
                        Nessuna abitudine ancora. Aggiungine una per iniziare!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Focus & Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Focus del Mese</CardTitle>
            <p className="text-sm text-muted-foreground">Scegli 2-3 abitudini prioritarie</p>
          </CardHeader>
          <CardContent>
            <Textarea
              data-testid="monthly-focus-input"
              value={monthlyNote.focus || ""}
              onChange={(e) => setMonthlyNote({ ...monthlyNote, focus: e.target.value })}
              placeholder="Es: Concentrarmi su meditazione e esercizio fisico..."
              className="min-h-[100px] rounded-sm"
            />
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Note</CardTitle>
            <p className="text-sm text-muted-foreground">Osservazioni / motivazione / ostacoli</p>
          </CardHeader>
          <CardContent>
            <Textarea
              data-testid="monthly-notes-input"
              value={monthlyNote.notes || ""}
              onChange={(e) => setMonthlyNote({ ...monthlyNote, notes: e.target.value })}
              placeholder="Riflessioni sul mese..."
              className="min-h-[100px] rounded-sm"
            />
          </CardContent>
        </Card>
      </div>

      <Button onClick={handleSaveNotes} data-testid="save-notes-btn" className="rounded-sm">
        Salva Note del Mese
      </Button>

      {/* Charts Section */}
      {habits.length > 0 && (
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Completamento Abitudini</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={habits.map(h => ({
                    name: h.name.length > 10 ? h.name.slice(0, 10) + '...' : h.name,
                    completamento: Math.round((getHabitTotal(h.habit_id) / daysInMonth) * 100),
                    color: h.color
                  }))}
                  layout="vertical"
                >
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Completamento']} />
                  <Bar dataKey="completamento" radius={[0, 4, 4, 0]}>
                    {habits.map((h, index) => (
                      <Cell key={`cell-${index}`} fill={h.color || '#1C1917'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <DisclaimerFooter />
      <HabitTipsButton />
    </div>
  );
};

export default HabitsPage;
